import { isIos } from '../pwa/platformDetection';
import { isIOS } from './iosLayout';

// Upper bound so an odd reading can't push bottom UI far up the screen.
const MAX_INSET = 120;
// Ignore sub-2px wobble while Safari's toolbar animates, to avoid re-layout every frame.
const MIN_CHANGE = 2;

let scheduleMeasure = null;

function isEditing() {
  const el = document.activeElement;
  return !!el && (el.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(el.tagName));
}

/**
 * iOS Safari's floating toolbar overlays the bottom of the page, and 100dvh does not
 * exclude it. Publishes the covered height as --browser-bar-inset on <html>, which
 * feeds --safe-bottom so the bottom nav, floating buttons and sheets clear the toolbar.
 *
 * Stays unset (0) on Android, desktop and the installed iOS app, which have no such
 * toolbar. While a text field is focused the on-screen keyboard shrinks the visual
 * viewport too; that is not toolbar coverage, so the inset is 0 while editing.
 */
export function startBrowserBarInset() {
  // iPhone clears the toolbar by scrolling the page, not by measuring it.
  if (isIOS()) return;
  const vv = typeof window !== 'undefined' ? window.visualViewport : null;
  if (!vv || !isIos()) return;

  const root = document.documentElement;
  let frame = 0;
  let last = 0;

  const measure = () => {
    frame = 0;
    let inset = 0;
    // Pinch-zoom shrinks the visual viewport; that is not toolbar coverage.
    if (!isEditing() && (!vv.scale || vv.scale <= 1.01)) {
      const covered = root.clientHeight - (vv.offsetTop + vv.height);
      inset = Math.min(MAX_INSET, Math.max(0, Math.round(covered)));
    }
    if (Math.abs(inset - last) < MIN_CHANGE && !(inset === 0 && last !== 0)) return;
    last = inset;
    root.style.setProperty('--browser-bar-inset', `${inset}px`);
  };

  const schedule = () => {
    if (!frame) frame = requestAnimationFrame(measure);
  };

  scheduleMeasure = schedule;
  measure();
  const opts = { passive: true };
  vv.addEventListener('resize', schedule, opts);
  vv.addEventListener('scroll', schedule, opts);
  window.addEventListener('orientationchange', schedule, opts);
  document.addEventListener('focusin', schedule, opts);
  document.addEventListener('focusout', schedule, opts);
}

/**
 * Re-measure on demand, e.g. when a bottom sheet opens. The app scrolls .main-content
 * rather than the document, so visualViewport events alone can leave a stale startup value.
 * No-op where startBrowserBarInset() bailed out (Android, desktop).
 */
export function refreshBrowserBarInset() {
  if (scheduleMeasure) scheduleMeasure();
}
